import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchRetiredPriests } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Calendar, Church, Award } from 'lucide-react';

interface RetiredPriest {
  id: string;
  name: string;
  title: string;
  photo?: string;
  phone?: string;
  retirementYear?: number;
  yearsOfService?: number;
  lastParish?: string;
  previousParishes?: string[];
  ordainedYear?: number;
  bio?: string;
  parish?: { name: string };
}

const RetiredPriests = () => {
  const [retiredPriests, setRetiredPriests] = useState<RetiredPriest[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadRetiredPriests = async () => {
      try {
        const response = await fetchRetiredPriests();

        const priests = response.data.priests;

        if (!priests || priests.length === 0) {
          throw new Error('No retired priests found');
        }

        setRetiredPriests(priests);
      } catch (error) {
        console.error('Error fetching retired priests:', error);

        // Fallback sample data
        const fallbackData: RetiredPriest[] = [
          {
            id: '1',
            name: 'Rev. Canon Olufemi Akanbi Olubi',
            title: 'Canon (Retired)',
            phone: '08034465889',
            lastParish: 'Christian Hope Anglican Church, Adaba',
            bio: 'A dedicated servant of God who spent over three decades ministering to the people of Ibadan South.',
          },
        ];

        setRetiredPriests(fallbackData);

        toast({
          title: 'Note',
          description: 'Using sample data. API connection will be established for live data.',
          variant: 'default',
        });
      } finally {
        setLoading(false);
      }
    };

    loadRetiredPriests();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading retired priests...</p>
        </div>
      </div>
    );
  }

  if (retiredPriests.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <p className="text-gray-500 text-lg">No retired priests information available.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Retired Priests
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Honoring our retired clergy who faithfully served the Diocese of Ibadan South
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {retiredPriests.map((priest) => (
            <Card key={priest.id} className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                  {priest.photo ? (
                    <img
                      src={priest.photo}
                      alt={priest.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span className="text-3xl text-white">👨‍💼</span>
                  )}
                </div>
                <CardTitle className="text-xl text-gray-900">{priest.name}</CardTitle>
                <p className="text-amber-600 font-semibold">{priest.title}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {priest.ordainedYear && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                      <div>
                        <span className="font-medium">Ordained:</span>
                        <p className="text-gray-600">{priest.ordainedYear}</p>
                      </div>
                    </div>
                  )}
                  {priest.yearsOfService && (
                    <div className="flex items-center">
                      <Award className="h-4 w-4 text-gray-500 mr-2" />
                      <div>
                        <span className="font-medium">Service:</span>
                        <p className="text-gray-600">{priest.yearsOfService} years</p>
                      </div>
                    </div>
                  )}
                </div>

                {priest.parish?.name && (
                  <div>
                    <div className="flex items-center mb-2">
                      <Church className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="font-medium text-gray-700">Last Parish:</span>
                    </div>
                    <p className="text-gray-600 ml-6">{priest.parish.name}</p>
                  </div>
                )}

                {priest.previousParishes && priest.previousParishes.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Previous Parishes:</span>
                    <ul className="mt-2 space-y-1">
                      {priest.previousParishes.map((parish, index) => (
                        <li key={index} className="text-gray-600 text-sm ml-4">
                          • {parish}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {priest.bio && (
                  <div>
                    <span className="font-medium text-gray-700">Biography:</span>
                    <p className="text-gray-600 text-sm mt-2 leading-relaxed">{priest.bio}</p>
                  </div>
                )}

                {priest.retirementYear && (
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm text-amber-600 font-medium">
                      Retired in {priest.retirementYear}
                    </p>
                  </div>
                )}

                {priest.phone && (
                  <div className="mt-2 text-sm">
                    <span className="font-medium text-gray-700">Phone:</span>
                    <p className="text-gray-600 ml-2">{priest.phone}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RetiredPriests;
